import { FC, memo, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AppTable } from '@/common/components';
import { AppRoutes, BaseQueryParams } from '@/common/interfaces';

import {
  ExternalRedirectsListTableDataObj,
  getExternalRedirectSourceDomainLabel,
  getExternalRedirectSourceEndpointLabel,
  getExternalRedirectTargetDomainLabel,
  getExternalRedirectTargetEndpointLabel,
  useGetAllExternalRedirectsQuery
} from '@/modules/ExternalRedirects';

import { columns } from './Columns';
import './ExternalRedirectsListView.scss';

interface Props {
  siteId: string;
  currentPage: number;
  externalRedirectsListItems: BaseQueryParams<typeof useGetAllExternalRedirectsQuery>['data']['items'];
  externalRedirectsListTotalItems: number;
  externalRedirectsListItemsPerPage: number;
  isLoading: boolean;
  handleChangePage: (page: number) => void;
}

export const ExternalRedirectsListView: FC<Props> = memo(({
  siteId,
  currentPage,
  externalRedirectsListItems,
  externalRedirectsListTotalItems,
  externalRedirectsListItemsPerPage,
  isLoading,
  handleChangePage
}) => {
  const navigate = useNavigate();
  const externalRedirectId = useParams()['*']?.split('/')[0];

  const goToRedirect = (id: string): void => {
    navigate(AppRoutes.ExternalRedirectsById.replace(':siteId', siteId).replace(':externalRedirectId', id));
  };

  const dataSource = useMemo<ExternalRedirectsListTableDataObj[]>(
    () => externalRedirectsListItems.map((externalRedirect) => ({
      id: externalRedirect.id,
      isActive: externalRedirect.isActive,
      matchMode: externalRedirect.matchMode,
      statusCode: externalRedirect.statusCode,
      forVisitor: externalRedirect.forVisitor,
      toType: externalRedirect.toType,
      sourceDomainLabel: getExternalRedirectSourceDomainLabel(externalRedirect),
      sourceEndpointLabel: getExternalRedirectSourceEndpointLabel(externalRedirect),
      targetDomainLabel: getExternalRedirectTargetDomainLabel(externalRedirect),
      targetEndpointLabel: getExternalRedirectTargetEndpointLabel(externalRedirect),
    })),
    [externalRedirectsListItems]
  );

  return (
    <AppTable<ExternalRedirectsListTableDataObj>
      className="rules-table"
      rowKey="id"
      showHeader={ false }
      loading={ isLoading }
      dataSource={ dataSource }
      rowClassName={
         (record) => (record.id === externalRedirectId
          ? 'rules-table__row rules-table__row--selected'
          : 'rules-table__row')
      }
      onRow={ (record ) => ({ onClick: () => goToRedirect(record.id) }) }
      pagination={{
        current: currentPage,
        total: externalRedirectsListTotalItems,
        pageSize: externalRedirectsListItemsPerPage,
        disabled: isLoading,
        onChange: handleChangePage,
        hideOnSinglePage: true,
        showSizeChanger: false,
      }}
      columns={columns }
    />
  );
});
